<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('school.payment.setup.feeGroup.pageTitle') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('school.payment.setup.feeGroup.pageTitle'),
            href: '/fee-groups'
          },
          {
            text: $t('edit'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-md-3">
            <form>
              <fieldset>
                <legend>{{ $t('school.payment.setup.feeGroup.group') }}</legend>
                <div class="row">
                  <div class="col-md-12 form-group">
                    <label class="required">{{
                      $t('school.payment.setup.feeGroup.groupName')
                    }}</label>
                    <input
                      v-model="model.fee_group_name"
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.has('fee_group_name') }"
                    />
                    <div
                      class="invalid-feedback"
                      v-if="errors.has('fee_group_name')"
                    >
                      {{ errors.first('fee_group_name') }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12 form-group">
                    <label class="required">{{
                      $t('school.payment.setup.feeGroup.paymentTerm')
                    }}</label>
                    <select
                      class="form-control"
                      v-model="model.payment_term"
                      :class="{ 'is-invalid': errors.has('payment_term') }"
                    >
                      <option value>{{ $t('select') }}</option>
                      <option value="Semesterly">{{
                        $t('feegroup.semesterly')
                      }}</option>
                      <option value="Yearly">{{
                        $t('feegroup.yearly')
                      }}</option>
                    </select>
                    <div
                      class="invalid-feedback"
                      v-if="errors.has('payment_term')"
                    >
                      {{ errors.first('payment_term') }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12 form-group">
                    <label class="required">{{
                      $t('school.payment.setup.feeGroup.amount')
                    }}</label>
                    <input
                      v-model.number="model.fee_amount"
                      type="number"
                      step="any"
                      class="form-control"
                      :class="{ 'is-invalid': errors.has('fee_amount') }"
                    />
                    <div
                      class="invalid-feedback"
                      v-if="errors.has('fee_amount')"
                    >
                      {{ errors.first('fee_amount') }}
                    </div>
                  </div>
                </div>
              </fieldset>
            </form>
          </div>
          <div class="col-md-9">
            <form>
              <fieldset>
                <legend>
                  {{ $t('school.payment.setup.feeGroup.component') }}
                </legend>
                <div id="container-table">
                  <table class="table table-hover ui-table">
                    <thead>
                      <tr>
                        <th>
                          {{ $t('school.payment.setup.feeGroup.academic') }}
                        </th>
                        <th style="width:15%" class="text-center">
                          {{ $t('school.payment.setup.feeGroup.fee') }}
                        </th>
                        <th style="width:15%" class="text-center">
                          {{ $t('school.payment.setup.feeGroup.paymentFee') }}
                        </th>
                        <th style="width:3%">
                          <a
                            href="#"
                            @click.prevent="addNewRow"
                            v-if="!fee_fetch"
                          >
                            <i class="fa fa-plus"></i>
                          </a>
                          <a href="#" v-else>
                            <i class="fa fa-spinner spin"></i>
                          </a>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(fee, index) in model.fee_setup_id_list"
                        :key="index"
                      >
                        <td>
                          <ts-search-select
                            v-model="fee.fee_setup_id"
                            @change="onFeeSetupChange(fee)"
                            :options="
                              feeSetupListExcludeSelected({
                                addRowFee: model.fee_setup_id_list,
                                selectedFee: fee.fee_setup_id
                              }).map(el => ({
                                text: el.fee_name_en,
                                value: el.fee_setup_id
                              }))
                            "
                          />
                          <div
                            class="validate"
                            v-if="
                              errors.has(
                                'fee_setup_id_list.' + index + '.fee_setup_id'
                              )
                            "
                          >
                            {{
                              errors.first(
                                'fee_setup_id_list.' + index + '.fee_setup_id'
                              )
                            }}
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            readonly
                            class="form-control only-bottom-border text-center"
                            v-model="fee.original_fee_amount"
                            :class="{
                              'is-invalid': errors.has(
                                'fee_setup_id_list.' +
                                  index +
                                  '.original_fee_amount'
                              )
                            }"
                          />
                          <div
                            class="invalid-feedback"
                            v-if="
                              errors.has(
                                'fee_setup_id_list.' +
                                  index +
                                  '.original_fee_amount'
                              )
                            "
                          >
                            {{
                              errors.first(
                                'fee_setup_id_list.' +
                                  index +
                                  '.original_fee_amount'
                              )
                            }}
                          </div>
                        </td>
                        <td>
                          <input
                            v-model="fee.discounted_fee"
                            type="number"
                            readonly
                            class="form-control only-bottom-border text-center"
                            :class="{
                              'is-invalid': errors.has(
                                'fee_setup_id_list.' + index + '.discounted_fee'
                              )
                            }"
                          />
                          <div
                            class="invalid-feedback"
                            v-if="
                              errors.has(
                                'fee_setup_id_list.' + index + '.discounted_fee'
                              )
                            "
                          >
                            {{
                              errors.first(
                                'fee_setup_id_list.' + index + '.discounted_fee'
                              )
                            }}
                          </div>
                        </td>
                        <td class="text-center" valign="middle">
                          <a
                            href="#"
                            class="text-danger"
                            @click.prevent="removeRow(fee)"
                          >
                            <i class="fa fa-trash mt-2"></i>
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="validate" v-if="errors.has('fee_setup_id_list')">
                    {{ errors.first('fee_setup_id_list') }}
                  </div>
                </div>
              </fieldset>
            </form>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-md-12 text-right">
            <ts-button
              color="info"
              outline
              class="btn btn-default mr-2"
              @click="$router.push({ name: 'fee-group' })"
              >{{ $t('cancel') }}</ts-button
            >
            <ts-button
              :waiting="waiting"
              class="btn btn-primary"
              @click.prevent="update"
              >{{ $t('update') }}</ts-button
            >
          </div>
        </div>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import _ from 'lodash'

export default {
  name: 'FeeGroupEdit',
  data () {
    return {
      model: {
        fee_group_id: '',
        fee_group_name: '',
        payment_term: '', // Semesterly, Yearly
        fee_amount: '',
        fee_setup_id_list: []
      },
      newRow: {
        fee_setup_id: '',
        original_fee_amount: '',
        discounted_fee: ''
      },
      errors: new Errors(),
      fee_fetch: true,
      waiting_save_add_new: false,
      waiting: false
    }
  },
  created () {
    this.setEditData()
  },
  computed: {
    ...mapGetters('feeGroup', ['feeSetupListExcludeSelected']),
    ...mapState('feeGroup', ['feeSetups', 'edit_data'])
  },
  methods: {
    ...mapActions('feeGroup', ['getFeeSetup']),
    addNewRow () {
      this.model.fee_setup_id_list.push(Object.assign({}, this.newRow))
    },
    removeRow (row) {
      this.model.fee_setup_id_list.splice(
        this.model.fee_setup_id_list.indexOf(row),
        1
      )
    },
    update () {
      this.waiting = true
      this.errors = new Errors()
      this.$store
        .dispatch('feeGroup/update', {
          id: this.model.fee_group_id,
          data: this.model
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'fee-group' })
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    async setEditData () {
      this.model.fee_group_id = this.edit_data.fee_group_id
      this.model.fee_group_name = this.edit_data.fee_group_name
      this.model.payment_term = this.edit_data.payment_term // Semesterly, Yearly
      this.model.fee_amount = this.edit_data.fee_amount
      this.model.fee_setup_id_list = []
      await this.getFeeSetup()
      this.fee_fetch = false
      if (!_.isEmpty(this.edit_data)) {
        this.edit_data.fee_setups.map(el => {
          this.model.fee_setup_id_list.push({
            fee_setup_id: el.fee_setup_id,
            original_fee_amount: el.fee_amount,
            discounted_fee: this.calculateDiscountFee(el.fee_amount)
          })
        })

        this.calculateFee()
      }
    },
    onFeeSetupChange (row) {
      row.original_fee_amount = row.fee_setup_id
        ? this.feeSetups.filter(f => f.fee_setup_id == row.fee_setup_id)[0]
            .fee_amount
        : ''
      this.calculateFee()
    },
    calculateDiscountFee (valueRow) {
      if (valueRow) {
        return (
          valueRow -
          (_.sumBy(this.model.fee_setup_id_list, 'original_fee_amount') -
            this.model.fee_amount) /
            this.model.fee_setup_id_list.length
        )
      }
      return 0
    },
    calculateFee () {
      this.model.fee_setup_id_list.map(el => {
        el.discounted_fee = this.calculateDiscountFee(el.original_fee_amount)
      })
    }
  },
  watch: {
    'model.fee_amount': function () {
      this.calculateFee()
    }
  },
  beforeRouteLeave (to, from, next) {
    this.$store.commit('feeGroup/RESET_STATE')
    this.$store.commit('feeGroup/SET_EDIT_DATA', [])
    next()
  }
}
</script>

<style lang="css" scoped>
#container-table {
  max-height: 30vh;
  height: 28vh;
  overflow-y: scroll;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
::-webkit-scrollbar {
  width: 3px; /* Remove scrollbar space */
  background: transparent; /* Optional: just make scrollbar invisible */
}
/* Optional: show position indicator in red */
::-webkit-scrollbar-thumb {
  background: rgb(194, 194, 194);
}
.table > thead > tr > th {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  z-index: 999;
}
.table > tbody > tr > td {
  padding-top: 5px !important;
  padding-bottom: 5px !important;
  border: none !important;
}
.only-bottom-border {
  border-top: none;
  border-left: none;
  border-right: none;
}
</style>
